import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import { appToast } from '../../../components/AppToast'
import { useCurrencyEntryScreen } from './hooks/useCurrencyEntryScreen'

const TIPS = [
  {
    icon: 'info',
    title: 'Code',
    body: 'Enter the ISO 4217 currency code such as INR, USD, EUR, GBP. This code uniquely identifies the currency across the system.',
  },
  {
    icon: 'sell',
    title: 'Symbol',
    body: 'Enter the currency symbol that appears in your financial reports wherever applicable. Ex. ₹, $, €. You may use character map for special symbols.',
  },
  {
    icon: 'label',
    title: 'Formal Name',
    body: 'Enter the full official name of the currency. Ex. Indian Rupee, US Dollars, Euro. This name is used in printed documents and exports.',
  },
  {
    icon: 'tag',
    title: 'Decimal Places',
    body: 'Enter the number of decimal places used for this currency. Ex. 2 for most currencies (Paise, Cents). Some currencies like JPY use 0.',
  },
  {
    icon: 'swap_horiz',
    title: 'Exchange Rate',
    body: 'Enter how many units of the base currency equal 1 unit of this currency. Ex. If base is INR and adding USD, enter 83.50 (1 USD = 83.50 INR). The base currency rate is always locked at 1.000000.',
  },
  {
    icon: 'tune',
    title: 'Rounding Method',
    body: 'Select how fractional amounts are rounded. Round Off applies standard mathematical rounding. Round Up always rounds to the next unit. Round Down always truncates.',
  },
]

export default function CurrencyMasterEntryScreen() {
  const { state, actions } = useCurrencyEntryScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const title = state.isEditing ? 'Edit Currency Details' : 'Register Currency'
  const subtitle = state.isEditing
    ? 'Update the details of an existing currency. The currency code cannot be changed.'
    : 'Register a new ISO currency for use across projects, billing, and financial reporting.'

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-7 pb-8">
      {/* Header */}
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <p className="inline-flex rounded-full bg-secondary-container px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-on-secondary-container">
            Masters Framework
          </p>
          <h1 className="mt-4 text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            {title}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-on-surface-variant">{subtitle}</p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/currency/list"
            className="inline-flex items-center justify-center rounded-md bg-surface-container-high px-8 py-3 text-sm font-semibold text-on-surface-variant"
          >
            Discard Changes
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-9 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">save</span>
            {state.success ? 'Saved! ✓' : state.isSaving ? 'Saving...' : state.isEditing ? 'Save Changes' : 'Save Currency'}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
          Loading currency details...
        </div>
      ) : (
        <section className="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_360px]">
          {/* Form Card */}
          <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8 xl:p-10">
            <div className="space-y-8">

              {/* Base Currency Notice */}
              {state.isBase && (
                <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
                  <span className="material-symbols-outlined mt-0.5 shrink-0 text-primary">lock</span>
                  <div>
                    <p className="text-sm font-bold text-primary">Base Currency — Exchange Rate Locked</p>
                    <p className="mt-1 text-xs leading-relaxed text-on-surface-variant">
                      This is the designated base reporting currency. Its exchange rate is permanently set to 1.000000 and cannot be modified. You may update the name, symbol, and format settings.
                    </p>
                  </div>
                </div>
              )}

              {/* Currency Code — disabled when editing */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Currency Code {!state.isEditing && <span className="text-error">*</span>}
                  </label>
                  {state.isEditing ? (
                    <span className="flex items-center gap-1 text-[10px] italic text-outline">
                      <span className="material-symbols-outlined text-xs">lock</span>
                      Cannot be changed
                    </span>
                  ) : (
                    <span className="text-[10px] italic text-outline">ISO 4217 Code</span>
                  )}
                </div>
                <input
                  type="text"
                  value={state.form.currencyCode}
                  onChange={(e) => !state.isEditing && actions.patchForm('currencyCode', e.target.value.toUpperCase())}
                  placeholder="e.g., USD, EUR, GBP"
                  maxLength={10}
                  readOnly={state.isEditing}
                  className={`w-full rounded-md border-0 px-6 py-4 text-2xl font-bold uppercase tracking-widest text-on-surface outline-none placeholder:text-outline-variant/70 ${
                    state.isEditing
                      ? 'cursor-not-allowed bg-surface-container-high opacity-70'
                      : 'bg-surface-container-low'
                  }`}
                />
              </div>

              {/* Symbol + Formal Name */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <label className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Symbol <span className="text-error">*</span>
                  </span>
                  <input
                    type="text"
                    value={state.form.symbol}
                    onChange={(e) => actions.patchForm('symbol', e.target.value)}
                    placeholder="e.g., $, €, ₹"
                    maxLength={10}
                    className="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-2xl text-on-surface outline-none placeholder:text-outline-variant/70"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Formal Name <span className="text-error">*</span>
                  </span>
                  <input
                    type="text"
                    value={state.form.currencyName}
                    onChange={(e) => actions.patchForm('currencyName', e.target.value)}
                    placeholder="e.g., US Dollars, Indian Rupee"
                    className="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-outline-variant/70"
                  />
                </label>
              </div>

              {/* Exchange Rate + Decimal Places */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                      Exchange Rate (to Base) {!state.isBase && <span className="text-error">*</span>}
                    </label>
                    {state.isBase && (
                      <span className="flex items-center gap-1 text-[10px] italic text-outline">
                        <span className="material-symbols-outlined text-xs">lock</span>
                        Locked at 1.000000
                      </span>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    value={state.form.exchangeRate}
                    onChange={(e) => !state.isBase && actions.patchForm('exchangeRate', e.target.value)}
                    placeholder="e.g., 83.5000"
                    readOnly={state.isBase}
                    className={`w-full rounded-md border-0 px-4 py-3.5 font-mono text-sm text-on-surface outline-none placeholder:text-outline-variant/70 ${
                      state.isBase
                        ? 'cursor-not-allowed bg-surface-container-high opacity-70'
                        : 'bg-surface-container-low'
                    }`}
                  />
                </div>

                <label className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Decimal Places
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="4"
                    value={state.form.decimalPlaces}
                    onChange={(e) => actions.patchForm('decimalPlaces', parseInt(e.target.value) || 0)}
                    className="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 font-mono text-sm text-on-surface outline-none"
                  />
                </label>
              </div>

              {/* Rounding Method */}
              <label className="grid gap-2">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                  Rounding Method
                </span>
                <AppDropdown
                  value={state.form.roundingMethod}
                  onChange={(value) => actions.patchForm('roundingMethod', value)}
                  options={state.roundingOptions}
                  triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface"
                />
              </label>

            </div>
          </article>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Completion Progress */}
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-6">
              <div className="flex items-end justify-between">
                <p className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                  Form Completion
                </p>
                <p className="text-2xl font-black text-primary">{state.completionPercent}%</p>
              </div>
              <div className="mt-3 h-2.5 w-full rounded-full bg-surface-variant">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${Math.max(8, state.completionPercent)}%` }}
                />
              </div>
              <p className="mt-3 text-xs text-outline">Auto-calculated from filled mandatory and optional fields.</p>
            </article>

            {/* TIPS Card */}
            <article className="rounded-[2rem] bg-surface-container-low p-6">
              <h3 className="text-sm font-black uppercase tracking-[0.14em] text-primary">Tips</h3>
              <p className="mt-2 text-xs text-on-surface-variant">
                {state.isEditing
                  ? 'Editing an existing currency. The currency code is locked.'
                  : 'Use this screen to register additional currencies for your organization.'}
              </p>
              <div className="mt-5 space-y-4">
                {TIPS.map((tip) => (
                  <div key={tip.title} className="flex items-start gap-3">
                    <span className="material-symbols-outlined mt-0.5 shrink-0 text-base text-primary">{tip.icon}</span>
                    <div>
                      <p className="text-sm font-bold text-on-surface">{tip.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-on-surface-variant">{tip.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  )
}
